import { z } from 'zod';
import { LIVE_CLASS_STATUSES, MEETING_PROVIDERS, WS_MAX_MESSAGE_LENGTH, MESSAGE_HISTORY_LIMIT_MAX, DASHBOARD_RANGES, } from '../constants';
export const loginSchema = z.object({
    identifier: z.string().trim().min(1, 'Email or phone number is required').max(254).refine((value) => z.string().email().safeParse(value).success || /^\+?[1-9]\d{9,14}$/.test(value), 'Enter a valid email address or phone number'),
    password: z.string().min(1, 'Password is required').max(128),
});
export const passwordSchema = z
    .string()
    .min(12, 'Password must be at least 12 characters')
    .max(128, 'Password must not exceed 128 characters')
    .regex(/[A-Z]/, 'Password must contain an uppercase letter')
    .regex(/[a-z]/, 'Password must contain a lowercase letter')
    .regex(/[0-9]/, 'Password must contain a number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain a special character');
export const registerSchema = z.object({
    name: z.string().trim().min(2, 'Name must be at least 2 characters').max(100),
    email: z.string().trim().toLowerCase().email('Invalid email address').max(254),
    phone: z.string().trim().regex(/^\+?[1-9]\d{9,14}$/, 'Enter a valid phone number'),
    password: passwordSchema,
    confirmPassword: z.string().max(128),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
});
/* ============================ LIVE CLASSES (Module 5) ============================ */
// POST /api/admin/users/:id/promote-instructor
export const promoteInstructorParamsSchema = z.object({
    id: z.coerce.number().int().positive(),
});
// POST /api/live-classes
export const createLiveClassSchema = z.object({
    title: z.string().min(3, 'Title must be at least 3 characters').max(200),
    description: z.string().max(5000).optional().nullable(),
    instructorId: z.coerce.number().int().positive('instructorId is required'),
    courseId: z.coerce.number().int().positive().optional().nullable(),
    categoryId: z.coerce.number().int().positive().optional().nullable(),
    tags: z.array(z.string().max(50)).max(20).optional(),
    capacity: z.coerce.number().int().min(1).max(100000).optional(),
    attendanceRule: z
        .object({
        thresholdPct: z.number().int().min(0).max(100).default(80),
        lateGraceMin: z.number().int().min(0).max(120).default(10),
    })
        .optional(),
    isPublished: z.boolean().optional().default(false),
});
// PATCH /api/live-classes/:id (instructor reassignment is admin-only)
export const updateLiveClassSchema = createLiveClassSchema
    .partial()
    .omit({ instructorId: true });
// POST /api/live-classes/:id/sessions (single or bulk series)
export const liveClassSessionSchema = z.object({
    title: z.string().max(200).optional().nullable(),
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    timezone: z.string().max(64).optional().default('UTC'),
    capacity: z.coerce.number().int().min(1).max(100000).optional().nullable(),
    meetingProvider: z.enum(MEETING_PROVIDERS).optional().default('AGORA'),
});
export const createLiveClassSessionsSchema = z.object({
    sessions: z.array(liveClassSessionSchema).min(1).max(100),
});
// PATCH /api/live-classes/sessions/:sessionId
export const updateLiveClassSessionSchema = z.object({
    title: z.string().max(200).optional().nullable(),
    startsAt: z.coerce.date().optional(),
    endsAt: z.coerce.date().optional(),
    timezone: z.string().max(64).optional(),
    capacity: z.coerce.number().int().min(1).max(100000).optional().nullable(),
    meetingProvider: z.enum(MEETING_PROVIDERS).optional(),
});
// GET /api/live-classes query params
export const liveClassQuerySchema = z.object({
    status: z.enum(LIVE_CLASS_STATUSES).optional(),
    courseId: z.coerce.number().int().positive().optional(),
    instructorId: z.coerce.number().int().positive().optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    q: z.string().max(200).optional(),
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
// GET /api/me/live-classes?scope=...
export const myLiveClassesQuerySchema = z.object({
    scope: z.enum(['upcoming', 'past', 'recorded']).default('upcoming'),
});
// Path params
export const liveClassIdParamsSchema = z.object({
    id: z.coerce.number().int().positive(),
});
export const liveClassSessionIdParamsSchema = z.object({
    sessionId: z.coerce.number().int().positive(),
});
/* ---- Real-time chat & Q&A (Phase 3, TRD §4.2/§4.3/§6.5) ---- */
// POST /api/live-classes/sessions/:sessionId/messages (REST fallback) + WS chat.send/qa.ask
export const createMessageSchema = z.object({
    messageType: z.enum(['CHAT', 'QUESTION', 'ANSWER', 'ANNOUNCEMENT']).default('CHAT'),
    content: z
        .string()
        .min(1, 'Message cannot be empty')
        .max(WS_MAX_MESSAGE_LENGTH, `Message cannot exceed ${WS_MAX_MESSAGE_LENGTH} characters`),
    replyToId: z.coerce.number().int().positive().optional().nullable(),
});
// POST /api/live-classes/messages/:id/vote (id parsed from the path server-side)
export const voteMessageSchema = z.object({
    messageId: z.coerce.number().int().positive(),
});
// PATCH /api/live-classes/messages/:id/pin | /hide | /answer
export const moderationActionSchema = z.object({
    action: z.enum(['pin', 'hide', 'answer']),
    // Only used by `pin`: true = pin, false = unpin.
    pinned: z.boolean().optional(),
});
// POST /api/live-classes/sessions/:sessionId/polls
export const createPollSchema = z.object({
    question: z
        .string()
        .min(1, 'Poll question is required')
        .max(WS_MAX_MESSAGE_LENGTH, `Poll question cannot exceed ${WS_MAX_MESSAGE_LENGTH} characters`),
    options: z
        .array(z.string().min(1, 'Option text is required').max(200))
        .min(2, 'A poll needs at least 2 options')
        .max(10, 'A poll can have at most 10 options'),
    allowMultiple: z.boolean().optional().default(false),
});
// POST /api/live-classes/sessions/:sessionId/polls/:pollId/vote
export const pollVoteSchema = z.object({
    optionId: z.string().min(1, 'optionId is required').max(100),
});
// GET /api/live-classes/sessions/:sessionId/messages?after=<id>&limit=
export const messagesQuerySchema = z.object({
    after: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().min(1).max(MESSAGE_HISTORY_LIMIT_MAX).optional(),
});
/* ============================ ADMIN DASHBOARD (Module 6) ============================ */
// GET /api/admin/dashboard/overview|realtime?days=7|14|30|90
export const dashboardQuerySchema = z.object({
    days: z
        .number()
        .int()
        .refine((value) => DASHBOARD_RANGES.includes(value), {
        message: 'days must be one of: 7, 14, 30, 90',
    })
        .default(30)
        .transform((value) => value),
});
// GET /api/admin/dashboard/activity?limit=...
export const dashboardActivityQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(50).default(10),
});
